import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import CustomModal from '../../custom/CustomModal';
import { CustomSelect } from '../../custom/CustomSelect';
import {
  extractFormValues,
  isNullValue,
  validateForm
} from '../../../utils/formUtil';
import {
  AvailableBox,
  OrderBox,
  SupplierBox
} from '../../ui/styled-views';
import API from '../../../data/api';
import { fNumber } from '../../../utils/formatNumber';
import CustomTextField from 'src/components/custom/CustomTextField';

const initAssignment = {
  depot: { value: "", required: true, error: false, errorMessage: 'Please select a depot' },
  fromTank: { value: "", required: true, error: false, errorMessage: 'Please select a tank to trasfer from' },
  toTank: { value: "", required: true, error: false, errorMessage: 'Please select a tank to transfer into' },
  volume: { value: "", required: true, error: false, errorMessage: 'Please specify volume transfer between tanks' },
  note: { value: "", required: false, error: false, },
};

const InterDepotTankTransfer = ({
  openModal,
  handleCloseModal,
}) => {

  const [submitted, setSubmitted] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [remainingVolume, setRemainingVolume] = useState(0);
  const [availableVolume, setAvailableVolume] = useState(0);
  const [product, setProduct]  = useState(null);
  const [depots, setDepots] = useState([]);
  const [supplyTanks, setSupplyTanks] = useState([]);
  const [receiveTanks, setReceiveTanks] = useState([]);

  useEffect(() => {

    setAssignment({ ...initAssignment });
    getDepots();

  }, [openModal]);

  const getDepots = async () => {
    let _depots = [];
    try {
      const response = await API.get('inventory/backoffice/depots?depot_type=storage');
      if (response.status === 200) {
        _depots = response.data.depots;
      } else {
        console.error('Error fetching depots: ', response.status);
      }
    } catch (error) {
      console.error('Error fetching depots: ', error);
    }
    setDepots(_depots);
  };

  const getDepotTanks = async (depot_id) => {
    if (isNullValue(depot_id)) {
      return;
    }

    let _tanks =[];
    try {
      const response = await API.get( `inventory/logistics/tanks?depot_id=${depot_id}`);
      if (response.status === 200) {
        _tanks=response.data
      } else {
        console.error('Error fetching depot tanks: ', response.status);
      }
    } catch (error) {
      console.error('Error fetching depot tanks: ', error);
    }
    setSupplyTanks(_tanks);
  };

  const handleSubmitAssignment = async () => {

    // If any of the inputs or required parameters is empty, return
    if (!validateForm(setAssignment, assignment) || isNullValue(product)) {

      console.log("Opps validation error")
      return;
    }

    const { depot, fromTank, toTank, volume, note } = extractFormValues(assignment);

    setSubmitted(true);
    try {
      const response = await API.post(`inventory/backoffice/create_internal_transfer`, {
        depot_id: depot,
        from_tank_id: fromTank,
        to_tank_id: toTank,
        product_id: product,
        volume: volume,
        note: note || null,
      });

      if (response.status === 200 || response.status === 201) {
        handleResetAndClose();
      } else {
        console.error('Error creating suborder assignment: ', response.status);
      }
    } catch (error) {
      console.error('Error creating suborder assignment: ', error);
    }
    setSubmitted(false);
  };

  const handleResetAndClose = () => {
    setRemainingVolume(0);
    setAvailableVolume(0);
    setProduct(null);
    setDepots([]);
    setSupplyTanks([]);
    setReceiveTanks([]);
    handleCloseModal();
  }

  const handleInputChange = (field, value, error = false, message = null) => {
    const _assignment = {
      ...assignment,
      [field]: {
        ...assignment[field],
        value: value,
        error: (isNullValue(value) && assignment[field]['required'] )|| error,
        errorMessage: message || assignment[field]['errorMessage'],
      },
    }
    setAssignment(_assignment);
  };

  const handleDepotSelection = (depotId) => {

    getDepotTanks(depotId);

    handleInputChange('depot', depotId, isNullValue(depotId))
  };

  const handleSupplyTankSelection = (tankId) => {

    const selectedTank = supplyTanks.find((t)=> t.tank_id === tankId);
    const otherTanks = supplyTanks?.filter((t) => t.tank_id !== tankId && t.product_id === selectedTank.product_id);
    setProduct(selectedTank.product_id);
    setAvailableVolume(selectedTank.available_volume)
    setReceiveTanks(otherTanks);

    handleInputChange('fromTank', tankId, isNullValue(tankId))
  };

  const handleReceiveTankSelection = (tankId) => {

    const selectedTank = receiveTanks.find((t)=> t.tank_id === tankId);
    setRemainingVolume(selectedTank.capacity - selectedTank.available_volume);

    handleInputChange('toTank', tankId, isNullValue(tankId))
  };

  return (
    <Paper>
      <CustomModal
        openModal={openModal}
        handleCloseModal={handleResetAndClose}
        modalTitle={`Depot Inter Tank Transfer`}
      >
        <>
          {/* <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <img style={{ height: 100 }}
              alt="Tanks"
              src="/assets/images/gas-storage-tanks.jpg"
            />
          </Box> */}

          <FormControl fullWidth sx={{ my: 2 }}>
            <CustomSelect
              labelId="depot-select-label"
              label={"Depot"}
              value={assignment?.depot.value}
              onChange={(e) => handleDepotSelection(e.target.value)}
              required={assignment?.depot.required}
              error={assignment?.depot.error}
              helperText={assignment?.depot.error && assignment?.depot.errorMessage}
              // disabled={isNotNullValue(orderData?.supplier_id)}
            >
              <MenuItem value="" disabled>
                <em>Select Depot</em>
              </MenuItem>
              {depots?.map((d) => (
                <MenuItem key={"depot-order-" + d.depot_id}
                  value={d.depot_id}
                >
                  {d.depot_name}
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl>

          <FormControl fullWidth sx={{ my: 2 }}>
            <CustomSelect
              labelId="from-tank-select-label"
              label={"From Tank"}
              value={assignment?.fromTank.value}
              onChange={(e) => handleSupplyTankSelection(e.target.value)}
              required={assignment?.fromTank.required}
              error={assignment?.fromTank.error}
              helperText={assignment?.fromTank.error && assignment?.fromTank.errorMessage}
              disabled={supplyTanks?.length < 1}
            >
              <MenuItem value="" disabled>
                <em>Select Tank</em>
              </MenuItem>
              {supplyTanks?.map((t) => (
                <MenuItem key={"from-tank-order-" + t.tank_id}
                  value={t.tank_id}
                >
                  <SupplierBox title="Tank Label" minWidth={150}>{t.tank_label}</SupplierBox>
                  <AvailableBox title="Current Level"> {fNumber(t.available_volume)}</AvailableBox>
                  <OrderBox title="Product">{t.product_name}</OrderBox>
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl>

          <FormControl fullWidth sx={{ my: 2 }}>
            <CustomSelect
              labelId="to-tank-select-label"
              label={"To Tank"}
              value={assignment?.toTank.value}
              onChange={(e) => handleReceiveTankSelection(e.target.value)}
              required={assignment?.toTank.required}
              error={assignment?.toTank.error}
              helperText={assignment?.toTank.error && assignment?.toTank.errorMessage}
              disabled={receiveTanks?.length < 1}
            >
              <MenuItem value="" disabled>
                <em>Select Tank</em>
              </MenuItem>
              {receiveTanks?.map((t) => (
                <MenuItem key={"to-tank-order-" + t.tank_id}
                  value={t.tank_id}
                >
                  <SupplierBox title="Tank Label" minWidth={150}>{t.tank_label}</SupplierBox>
                  {/* <AllocatedBox title="Total Capacity">{fNumber(t.capacity)}</AllocatedBox> */}
                  <AvailableBox title="Empty Level"> {fNumber(t.capacity - t.available_volume)}</AvailableBox>
                  <OrderBox title="Product">{t.product_name}</OrderBox>
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl>

          <CustomTextField sx={{ my: 1 }}
            fullWidth
            type="number"
            id="volume"
            variant="outlined"
            name='volume'
            label={`Volume`}
            value={assignment?.volume.value}
            onChange={(e) => {
              const value = e.target.value;
              const error = (value < 1 || value > availableVolume || value > remainingVolume);
              const message = error? `Please specify volume and cannot be more than ${Math.min(availableVolume, remainingVolume)}`: null;
              handleInputChange('volume', value, error, message);
            }}
            required={assignment?.volume.required}
            error={assignment?.volume.error}
            helperText={assignment?.volume.error && assignment?.volume.errorMessage}
            disabled={availableVolume <= 0 || remainingVolume <= 0}
          />

          <CustomTextField sx={{ my: 1 }}
            fullWidth
            id="note"
            variant="outlined"
            name='note'
            label={`Note`}
            onChange={(e) => handleInputChange("note", e.target.value)}
            value={assignment?.note.value}
            required={assignment?.note.required}
            error={assignment?.note.error}
            helperText={assignment?.note.error && assignment?.note.errorMessage}
          />
        
          <Box sx={{ display: 'flex', justifyContent: 'right', mt: 2 }}>
            <Button
              variant='contained'
              color="inherit"
              onClick={handleResetAndClose}
            >
              Cancel
            </Button>
            <LoadingButton
              sx={{ ml: 2, }}
              variant="contained"
              loading={submitted}
              onClick={handleSubmitAssignment}
              // disabled={isRemainingVolumeZero}
            >
              Submit
            </LoadingButton>
          </Box>
        </>
      </CustomModal>
    </Paper>
  )
}

InterDepotTankTransfer.propTypes = {

  /** Selected orderData details */
  orderData: PropTypes.object.isRequired,

  /** List of suitable depots */
  depota: PropTypes.array.isRequired,

  /** Flag to toggling openning of modal */
  openModal: PropTypes.bool,

  /** Callback function when closing modal */
  handleCloseModal: PropTypes.func.isRequired,

  /** Callback function after assignment of suborder */
  handleSubmitCallback: PropTypes.func.isRequired,

}

export default InterDepotTankTransfer
